import { QuoteStatusEnum } from '@src/types/types';
import type { WritableDraft } from 'immer';
import {
  FieldValidationLevel,
  type FieldValidationRule,
  type NumericField,
  validatePrecision,
} from '../../../../fields';
import { format, toBigWithDefault } from '../../../../utils';
import { validationMessages } from '../OrderSlice/validation';
import { getCurrenciesIncrement, getSecurityAndSide } from './selector';
import type { SwapState } from './types';

export function validateQuantities(state: WritableDraft<SwapState>) {
  validateFromQty(state);
  validateToQty(state);
}

export function validateToQty(state: WritableDraft<SwapState>) {
  state.form.toQty = state.form.toQty.validate(
    [
      inputAwareQuantitySymbolValidation('toQty'),
      inputAwareQuantityMinSizeValidation('toQty'),
      quoteAwareValidation('toQty'),
    ],
    state
  );
}

export function validateFromQty(state: WritableDraft<SwapState>) {
  state.form.fromQty = state.form.fromQty.validate(
    [
      inputAwareQuantitySymbolValidation('fromQty'),
      fromQuantityTradingLimitsValidation,
      inputAwareQuantityMinSizeValidation('fromQty'),
      quoteAwareValidation('fromQty'),
    ],
    state
  );
}

const quoteAwareValidation = (
  input: 'toQty' | 'fromQty'
): FieldValidationRule<NumericField, WritableDraft<SwapState>> => {
  return (field, context) => {
    if (!context) {
      return null;
    }
    const { quote, quoteRequest } = context;
    if (!quote || !quoteRequest) {
      return null;
    }
    if (quote.QuoteStatus === QuoteStatusEnum.Rejected && quote.Text) {
      const { fromCurrencyDetails, toCurrencyDetails } = getCurrenciesIncrement(context);
      if (
        (quote.Currency === fromCurrencyDetails?.currency?.Symbol && input === 'fromQty') ||
        (quote.Currency === toCurrencyDetails?.currency?.Symbol && input === 'toQty')
      ) {
        return { message: quote.Text, level: FieldValidationLevel.Error };
      }
    }

    return null;
  };
};

const inputAwareQuantitySymbolValidation = (input: 'toQty' | 'fromQty') => {
  const quantitySymbolValidation: FieldValidationRule<NumericField, WritableDraft<SwapState>> = (field, context) => {
    if (!context) {
      return null;
    }
    const { fromCurrencyDetails, toCurrencyDetails } = getCurrenciesIncrement(context);

    let minIncr: string;
    let currency = '';
    if (input === 'toQty') {
      minIncr = toCurrencyDetails?.minIncrement ?? '';
      currency = toCurrencyDetails?.currency?.Symbol ?? '';
    } else {
      minIncr = fromCurrencyDetails?.minIncrement ?? '';
      currency = fromCurrencyDetails?.currency?.Symbol ?? '';
    }

    if (!toBigWithDefault(field.value, 0).eq(0) && !validatePrecision(minIncr, field.value)) {
      return {
        message: context.intl.formatMessage(validationMessages.minIncrementIsMinIncr, {
          minIncr,
          currency,
        }),
        level: FieldValidationLevel.Error,
      };
    }

    return null;
  };
  return quantitySymbolValidation;
};

const fromQuantityTradingLimitsValidation: FieldValidationRule<NumericField, WritableDraft<SwapState>> = (
  field,
  context
) => {
  if (!context?.dependencies.tradingLimitsValidation) {
    return null;
  }
  const { reject, warn, limit } = context.dependencies.tradingLimitsValidation;
  if (!limit) {
    return null;
  }

  if (reject) {
    return {
      message: context.intl.formatMessage(validationMessages.quantityExceedsRejectionThreshold, {
        threshold: format(limit.RejectThreshold),
        currency: limit.ThresholdCurrency,
      }),
      level: FieldValidationLevel.Error,
    };
  }
  if (warn) {
    return {
      message: context.intl.formatMessage(validationMessages.quantityExceedsWarningThreshold, {
        threshold: format(limit.WarnThreshold),
        currency: limit.ThresholdCurrency,
      }),
      level: FieldValidationLevel.Warning,
    };
  }
  return null;
};

const inputAwareQuantityMinSizeValidation = (input: 'toQty' | 'fromQty') => {
  const quantityMoreThanMinSize: FieldValidationRule<NumericField, WritableDraft<SwapState>> = (field, context) => {
    if (!context) {
      return null;
    }

    const security = getSecurityAndSide(context)?.security;
    if (!security) {
      return null;
    }

    if (input === 'toQty') {
      const { MinimumSize, MinimumAmount, BaseCurrency } = security;
      const { toCurrencyDetails } = getCurrenciesIncrement(context);

      const minValue = toCurrencyDetails?.currency?.Symbol === BaseCurrency ? MinimumSize : MinimumAmount;
      const comparableValue = toBigWithDefault(field.value, 0);
      if (minValue && !comparableValue.eq(0) && toBigWithDefault(field.value, 0).lt(minValue)) {
        return {
          message: context.intl.formatMessage(validationMessages.minQuantityIsMinSizeCurrency, {
            minSize: minValue,
            currency: toCurrencyDetails?.currency?.Symbol,
          }),
          level: FieldValidationLevel.Error,
        };
      }
    } else if (input === 'fromQty') {
      const { MinimumSize, MinimumAmount, BaseCurrency } = security;
      const { fromCurrencyDetails } = getCurrenciesIncrement(context);

      const minValue = fromCurrencyDetails?.currency?.Symbol === BaseCurrency ? MinimumSize : MinimumAmount;
      const comparableValue = toBigWithDefault(field.value, 0);

      if (minValue && !comparableValue.eq(0) && comparableValue.lt(minValue)) {
        return {
          message: context.intl.formatMessage(validationMessages.minQuantityIsMinSizeCurrency, {
            minSize: minValue,
            currency: fromCurrencyDetails?.currency?.Symbol,
          }),
          level: FieldValidationLevel.Error,
        };
      }
    }
    return null;
  };
  return quantityMoreThanMinSize;
};
